// Segments: named stretches of a track that every ride times, lap after lap (ADR 0031). A climb from its foot to its
// summit, the final sprint, or a stretch the track's author drew. The race engine clocks every rider through each of
// them (race.ts timeSegments); the best pass of a ride becomes a record the server keeps per player, and a tour stage
// may contest a segment for jersey points: fastest through it, or first past its end (events.ts, scoring.ts).
import { mod, courseSamples, type Track } from './track.js';

export type SegmentKind = 'climb' | 'sprint' | 'sector';
export const SEGMENT_KINDS: SegmentKind[] = ['climb', 'sprint', 'sector'];
/**
 * A segment of a track: `from` and `to` are positions on one lap (m from the start line). On a loop a segment may
 * wrap across the line (`to` < `from`); on an open road it never does. `cat`: climb category (1 hard … 4), climbs only.
 */
export interface SegmentDef { id: string; name: string; kind: SegmentKind; from: number; to: number; cat?: number }
export const SEGMENT_LIMITS = { perTrack: 8, minLength: 100, id: 40, name: 60 } as const;

// ---------- Climbs ----------
export interface Climb { start: number; top: number; gain: number; grade: number; cat: number }
const climbCache = new Map<string, Climb[]>();

/** Climbs of a track: from each trough to the next summit, with hysteresis to ignore bumps. Category 1 (hard) to 4. */
export function climbsOf(tr: Track): Climb[] {
  const hit = climbCache.get(tr.id);
  if (hit) return hit;
  // round a loop from its lowest point; an open road from its start line to its finish
  const N = tr.N, y = tr.y, last = tr.open ? courseSamples(tr) - 1 : N;
  let i0 = 0;
  if (!tr.open) for (let i = 1; i < N; i++) if (y[i] < y[i0]) i0 = i;
  const out: Climb[] = [];
  let lowI = i0, peakI = i0, climbing = false;
  for (let k = 1; k <= last; k++) {
    const i = (i0 + k) % N;
    if (!climbing) {
      if (y[i] < y[lowI]) lowI = i;
      else if (y[i] - y[lowI] >= 6) { climbing = true; peakI = i; }
    } else if (y[i] > y[peakI]) peakI = i;
    // a climb ends 4 m below its summit, or with the road: an open road may finish at the top
    if (climbing && (y[peakI] - y[i] >= 4 || k === last)) {
      const gain = y[peakI] - y[lowI], len = mod(peakI - lowI, N) * tr.step || 1, grade = gain / len;
      let cat = gain >= 70 ? 1 : gain >= 35 ? 2 : gain >= 15 ? 3 : 4;
      if (grade > 0.06 && cat > 1) cat--;
      out.push({ start: lowI * tr.step, top: peakI * tr.step, gain, grade, cat });
      climbing = false; lowI = i;
    }
  }
  climbCache.set(tr.id, out);
  return out;
}

export const KOM_LABEL = (cat: number) => (cat <= 0 ? 'beyond category' : `category ${cat}`);

// ---------- Segments of a track ----------
/** Length of a segment (m), across the line when it wraps. */
export const segmentLength = (seg: Pick<SegmentDef, 'from' | 'to'>, lap: number): number => (seg.to > seg.from ? seg.to - seg.from : mod(seg.to - seg.from, lap) || lap);

/** Net climbing of a segment (m, from its start to its end; negative downhill). */
export function segmentGain(tr: Track, seg: Pick<SegmentDef, 'from' | 'to'>): number {
  const at = (m: number) => tr.y[Math.min(tr.N - 1, Math.max(0, Math.round(m / tr.step)))];
  return at(seg.to) - at(seg.from);
}

/** Key of a segment record: the track, then the segment. */
export const segmentKey = (trackId: string, segmentId: string): string => `${trackId}/${segmentId}`;

const segmentCache = new Map<string, SegmentDef[]>();

/** Length of the final sprint (m): a fifth of the lap, between 200 and 500 m, rounded to 50 m. */
export const finishSprintLength = (lap: number): number => Math.min(500, Math.max(200, Math.round((lap * 0.2) / 50) * 50));

/**
 * The segments of a track: the ones its author drew (`Track.segments`, bounded), otherwise the ones read from the road:
 * every climb from its foot to its summit (long enough to be timed), then the final sprint before the line.
 */
export function segmentsOf(tr: Track): SegmentDef[] {
  const hit = segmentCache.get(tr.id);
  if (hit) return hit;
  const out: SegmentDef[] = tr.segments?.length ? boundSegments(tr.segments, tr) : autoSegments(tr);
  segmentCache.set(tr.id, out);
  return out;
}

function autoSegments(tr: Track): SegmentDef[] {
  // whole metres, and never past the line: a segment ending on the finish is clocked in the step that finishes the race
  const out: SegmentDef[] = [], end = Math.floor(tr.lap), at = (m: number) => Math.min(end, Math.round(m));
  const climbs = climbsOf(tr).filter(c => segmentLength({ from: c.start, to: c.top }, tr.lap) >= SEGMENT_LIMITS.minLength).slice(0, SEGMENT_LIMITS.perTrack - 1);
  climbs.forEach((c, i) => out.push({ id: `climb-${i + 1}`, name: climbs.length > 1 ? `Climb ${i + 1}` : 'The climb', kind: 'climb', from: Math.min(end - 1, at(c.start)), to: at(c.top), cat: c.cat }));
  const len = finishSprintLength(tr.lap);
  if (tr.lap >= 2 * len) out.push({ id: 'finish', name: 'Final sprint', kind: 'sprint', from: end - len, to: end });
  return out;
}

/** An author's segments, within the track and the limits; a bad one is dropped, never guessed. */
export function boundSegments(raw: unknown, tr: Pick<Track, 'lap' | 'open'>): SegmentDef[] {
  if (!Array.isArray(raw)) return [];
  const out: SegmentDef[] = [], ids = new Set<string>();
  for (const s of raw as Record<string, unknown>[]) {
    if (!s || typeof s !== 'object') continue;
    const id = String(s.id ?? '').replace(/[^\w-]/g, '').slice(0, SEGMENT_LIMITS.id), name = String(s.name ?? '').replace(/[<>]/g, '').trim().slice(0, SEGMENT_LIMITS.name);
    const from = Math.round(Number(s.from)), to = Math.round(Number(s.to)), kind = SEGMENT_KINDS.includes(s.kind as SegmentKind) ? (s.kind as SegmentKind) : 'sector';
    if (!id || ids.has(id) || !Number.isFinite(from) || !Number.isFinite(to) || from < 0 || to < 0 || from >= tr.lap || to > tr.lap || from === to) continue;
    if (tr.open && to < from) continue; // an open road does not wrap
    if (segmentLength({ from, to }, tr.lap) < SEGMENT_LIMITS.minLength) continue;
    const cat = kind === 'climb' && Number.isFinite(Number(s.cat)) ? Math.min(4, Math.max(0, Math.round(Number(s.cat)))) : undefined;
    out.push({ id, name: name || id, kind, from, to, ...(cat != null ? { cat } : {}) });
    ids.add(id);
    if (out.length >= SEGMENT_LIMITS.perTrack) break;
  }
  return out;
}

// ---------- Timing ----------
/**
 * Instant a rider who went from `prevS` to `s` during the step ending at `t` (length `dt`) crossed `mark`, by linear
 * interpolation; null when they did not. The same rule times every rider on the client and on the game server.
 */
export function crossingAt(prevS: number, s: number, mark: number, t: number, dt: number): number | null {
  if (!(prevS < mark && s >= mark)) return null;
  return t - dt * ((s - mark) / (s - prevS || 1));
}

/** A rider's pass through a segment: the lap it belongs to and its entry instant (race seconds). */
export interface SegmentEntry { lap: number; t: number }
/** A completed pass: the rider, the lap, the time through the segment (s). */
export interface SegmentPass { id: number; lap: number; time: number }
/**
 * Live timing of one segment for a field of riders, kept by the race (race.ts). `best`: each rider's best pass so far;
 * `orders`: the riders in the order they completed the segment, lap by lap (lap 0 first).
 */
export interface SegmentRun { def: SegmentDef; length: number; entry: Map<number, SegmentEntry>; best: Map<number, number>; passes: Map<number, number>; orders: number[][] }

export const segmentRun = (def: SegmentDef, lap: number): SegmentRun => ({ def, length: segmentLength(def, lap), entry: new Map(), best: new Map(), passes: new Map(), orders: [] });

/**
 * Advances one rider through a segment for the step ending at `t`: notes their entry when they cross the start on a
 * lap, and returns the pass they just completed, if any. Positions are cumulative (`s` grows lap after lap);
 * `maxLap` bounds the laps looked at (0 on an open road, where the run-out past the finish must not start a pass).
 */
export function segmentStep(g: SegmentRun, id: number, prevS: number, s: number, t: number, dt: number, lap: number, maxLap: number): SegmentPass | null {
  if (s <= prevS) return null;
  const from = g.def.from, en = g.entry.get(id);
  // the exit of the pass in progress first: a segment that wraps ends on the next lap
  if (en) {
    const at = crossingAt(prevS, s, en.lap * lap + from + g.length, t, dt);
    if (at != null) {
      g.entry.delete(id);
      const time = Math.max(0.01, +(at - en.t).toFixed(2));
      const best = g.best.get(id);
      if (best == null || time < best) g.best.set(id, time);
      g.passes.set(id, (g.passes.get(id) ?? 0) + 1);
      (g.orders[en.lap] ??= []).push(id);
      return { id, lap: en.lap, time };
    }
  }
  // then an entry: the lap the rider is in now, or the one they just left when the step crosses the line
  const k1 = Math.floor(s / lap), k0 = Math.max(0, Math.floor(prevS / lap));
  for (let k = k0; k <= Math.min(k1, maxLap); k++) {
    const at = crossingAt(prevS, s, k * lap + from, t, dt);
    if (at != null) { g.entry.set(id, { lap: k, t: at }); break; }
  }
  return null;
}
